import { Box, Grid, Paper, Stack, Typography } from "@mui/material";

function PreviewCard({ children, sx, dark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        border: 1,
        borderColor: dark ? "rgba(99,102,241,0.15)" : "#e2e8f0",
        bgcolor: dark ? "rgba(15,23,42,0.6)" : "#fff",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function FinancePreview({ theme }) {
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        {[
          { label: "Net Portfolio Value", value: "$1,248,392.40" },
          { label: "Available Liquid Cash", value: "$342,091.15" },
          { label: "Active Positions", value: "18 Positions" },
          { label: "Authorized Credit Line", value: "$150,000.00" },
        ].map((card) => (
          <Grid key={card.label} size={{ xs: 6, md: 3 }}>
            <PreviewCard>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", mb: 0.75 }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 900, color: theme.text }}>{card.value}</Typography>
            </PreviewCard>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <PreviewCard>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 2 }}>Asset Allocation</Typography>
            <Stack spacing={1.5}>
              {["US Equities", "Cash & Liquids", "CryptoAssets", "Bonds"].map((asset) => (
                <Box key={asset}>
                  <Stack direction="row" sx={{ justifyContent: "space-between", fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                    <span>{asset}</span>
                    <span>XX%</span>
                  </Stack>
                  <Box sx={{ width: "100%", height: 8, bgcolor: "#f1f5f9", borderRadius: 1 }} />
                </Box>
              ))}
            </Stack>
          </PreviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <PreviewCard sx={{ height: "100%" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ height: 120, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0" }} />
          </PreviewCard>
        </Grid>
      </Grid>
    </Stack>
  );
}

function InsurancePreview({ theme }) {
  return (
    <Stack spacing={3}>
      <PreviewCard sx={{ borderColor: "#f1e9d2" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: theme.text, mb: 2.5 }}>Your Active Policies</Typography>
        <Grid container spacing={2}>
          {[
            { title: "Auto Insurance Premium", cover: "$250,000 Liability", item: "Tesla Model 3 (2023)" },
            { title: "Homeowners Gold Coverage", cover: "$750,000 Replacement", item: "Primary Residence" },
          ].map((pol) => (
            <Grid key={pol.title} size={{ xs: 12, sm: 6 }}>
              <Box sx={{ p: 2, border: "1px solid #f1f5f9", borderRadius: 1.5, bgcolor: "#faf8f5" }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: theme.text }}>{pol.title}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.primary, mt: 1 }}>{pol.cover}</Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>{pol.item}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </PreviewCard>
      <PreviewCard sx={{ borderColor: "#f1e9d2" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: theme.text, mb: 2 }}>Active Claims Tracker</Typography>
        <Box sx={{ bgcolor: "#faf8f5", p: 2.5, borderRadius: 1.5, border: "1px solid #cbd5e1" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Claim #CLM-2026-092</Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>Type: Auto Property Damage</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {["Filed", "Review", "Assessment", "Payout"].map((step) => (
              <Box key={step} sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: theme.primary, flexShrink: 0 }} />
            ))}
          </Stack>
        </Box>
      </PreviewCard>
    </Stack>
  );
}

function HealthcarePreview({ theme }) {
  return (
    <Stack spacing={3}>
      <PreviewCard sx={{ borderColor: "#e9efef" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: theme.primary, mb: 2 }}>Appointments & Active Consultations</Typography>
        <Box sx={{ bgcolor: "#f0fdfa", p: 2.5, borderRadius: 2, border: `1.5px solid ${theme.primary}22` }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: theme.text }}>Cardiology Consultation Checkup</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>Dr. Sarah Jenkins • Room #4 Telehealth Terminal</Typography>
        </Box>
      </PreviewCard>
      <Grid container spacing={2}>
        {["Heart Rate", "Blood Pressure", "O2 Saturation", "Temperature"].map((v) => (
          <Grid key={v} size={{ xs: 6, md: 3 }}>
            <PreviewCard sx={{ borderColor: "#e9efef", textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>{v}</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: theme.primary, mt: 0.5 }}>--</Typography>
            </PreviewCard>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

function GovernmentPreview({ theme }) {
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <PreviewCard>
          <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>Citizen Credentials</Typography>
          <Stack spacing={1.25}>
            {["Digital ID Level 3", "Tax Filing Status", "Benefits Enrollment"].map((row) => (
              <Stack key={row} direction="row" sx={{ justifyContent: "space-between", fontSize: 13, py: 0.75, borderBottom: "1px solid #f1f5f9" }}>
                <Typography sx={{ fontWeight: 600 }}>{row}</Typography>
                <Typography sx={{ color: "#10b981", fontWeight: 800 }}>Verified</Typography>
              </Stack>
            ))}
          </Stack>
        </PreviewCard>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <PreviewCard>
          <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>Document Vault</Typography>
          <Stack spacing={1}>
            {["Passport Scan", "Tax Return 2025", "Benefits Letter"].map((doc) => (
              <Box key={doc} sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e2e8f0", fontSize: 13, fontWeight: 600 }}>
                {doc}
              </Box>
            ))}
          </Stack>
        </PreviewCard>
      </Grid>
    </Grid>
  );
}

function CorporatePreview() {
  return (
    <PreviewCard dark>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#a855f7", mb: 2 }}>Unified Single Sign-On Apps</Typography>
      <Grid container spacing={1.5}>
        {["Slack Enterprise", "GitHub Dev Node", "AWS Console", "Jira Software", "Figma Design", "Datadog Stats"].map((app) => (
          <Grid key={app} size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: "rgba(30,41,59,0.5)", border: "1px solid rgba(255,255,255,0.05)" }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#f8fafc" }}>{app}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </PreviewCard>
  );
}

const PREVIEWS = {
  finance: FinancePreview,
  insurance: InsurancePreview,
  healthcare: HealthcarePreview,
  government: GovernmentPreview,
  corporate: CorporatePreview,
};

export default function PortalPreviewBlur({ industry, theme }) {
  const Preview = PREVIEWS[industry] || FinancePreview;

  return (
    <Box sx={{ position: "relative", width: "100%", mt: { xs: 2, md: 3.75 } }}>
      <Box
        className="portal-preview-blur"
        sx={{
          filter: { xs: "blur(5px)", md: "blur(6px)" },
          opacity: { xs: 0.45, md: 0.35 },
          pointerEvents: "none",
          userSelect: "none",
          maxHeight: { xs: 220, sm: 280, lg: "none" },
          overflow: { xs: "hidden", lg: "visible" },
        }}
      >
        <Preview theme={theme} />
      </Box>
    </Box>
  );
}
